import { match } from 'ts-pattern'

import { ConditionalFunctionString } from '../../conditions.types'
import type { SemanticType } from '../../../semanticTypes'
import { sameAssetIds, samePaymentTerms, sameRawText } from '../runtimeHelpers'
import {
  isQuoteComparisonSubOperator,
  pricingOutcomeExpression,
  readPricingComparison,
  type QuoteComparisonValue,
  type QuotePricingComparison
} from './quoteComparison'
import { scopedSemanticTypeRuntimePath } from './utils'

/**
 * Configuration for the quote-template comparison factories. The semantic type
 * is the template's side of the comparison, published by the property's
 * `valueSupportingTask` and scoped per template — never by an upstream node.
 */
export type QuoteTemplateFunctionStringConfig = {
  templateSemanticType: SemanticType
}

/**
 * Pricing is compared by an executor rather than in the expression, so its
 * factory takes the semantic type of each outcome the executor publishes — one
 * per pricing comparison the row can choose.
 */
export type QuoteTemplatePricingFunctionStringConfig = {
  comparisonSemanticTypes: Record<QuotePricingComparison, SemanticType>
}

/**
 * Shape stored on the condition's `value` for every QUOTE_TEMPLATE operator.
 * `templateId` picks which template to compare against — and doubles as the
 * scope for the supporting task's runtime key.
 */
export type QuoteTemplateConditionValue = QuoteComparisonValue & {
  templateId?: string
}

export const readQuoteTemplateValue = (
  value: unknown
): QuoteTemplateConditionValue => {
  if (typeof value === 'object' && value !== null) {
    return value as QuoteTemplateConditionValue
  }
  return {}
}

/**
 * Factory for QUOTE_TEMPLATE.ATTACHMENTS. Compares the quote's attachments
 * against the chosen template's, by set of asset ids.
 */
export const quoteTemplateAttachments = (
  config: QuoteTemplateFunctionStringConfig
): ConditionalFunctionString =>
  quoteTemplateComparison({ compare: sameAssetIds, config })

/**
 * Factory for QUOTE_TEMPLATE.RAW_TEXT. Compares the quote's standard text
 * against the chosen template's, as text.
 */
export const quoteTemplateRawText = (
  config: QuoteTemplateFunctionStringConfig
): ConditionalFunctionString =>
  quoteTemplateComparison({ compare: sameRawText, config })

/**
 * Factory for QUOTE_TEMPLATE.PAYMENT_TERMS. Compares the quote's payment terms
 * against the chosen template's, as a day count.
 */
export const quoteTemplatePaymentTerms = (
  config: QuoteTemplateFunctionStringConfig
): ConditionalFunctionString =>
  quoteTemplateComparison({ compare: samePaymentTerms, config })

/**
 * Factory for QUOTE_TEMPLATE.PRICING. Reads the outcome `compare_quote_pricing`
 * published for the chosen template and pricing comparison.
 *
 * An unconfigured row compares as `false`, as the other fields do.
 */
export const quoteTemplatePricing =
  (
    config: QuoteTemplatePricingFunctionStringConfig
  ): ConditionalFunctionString =>
  ({ value }) => {
    const { templateId, operator } = readQuoteTemplateValue(value)
    if (!templateId) return 'false'
    if (!isQuoteComparisonSubOperator(operator)) return 'false'

    return pricingOutcomeExpression({
      semanticType:
        config.comparisonSemanticTypes[readPricingComparison(value)],
      scope: templateId,
      subOperator: operator
    })
  }

/**
 * Builds a template comparison from an equality runtime helper. Each field
 * supplies only that helper — IS_NOT_EQUAL is its negation. An unconfigured row
 * compares as `false`; validation surfaces the gap rather than generation
 * throwing.
 *
 *   compare( property                   , scoped template path      )
 *          | quote side, the operator's | published by the row's
 *          | valueSemanticType          | valueSupportingTask
 */
const quoteTemplateComparison =
  ({
    compare,
    config
  }: {
    compare: (quoteValue: string, templateValue: string) => string
    config: QuoteTemplateFunctionStringConfig
  }): ConditionalFunctionString =>
  ({ property, value }) => {
    const { templateId, operator } = readQuoteTemplateValue(value)
    if (!templateId) return 'false'
    if (!isQuoteComparisonSubOperator(operator)) return 'false'

    const equal = compare(
      property,
      scopedSemanticTypeRuntimePath({
        semanticType: config.templateSemanticType,
        scope: templateId
      })
    )

    return match(operator)
      .with('IS_EQUAL', () => equal)
      .with('IS_NOT_EQUAL', () => `!(${equal})`)
      .exhaustive()
  }
